export interface FullPolicyData extends PolicyDetail, Policy {}

export interface PolicyDetail {
  accountNumber: string;
  altPhoneNumber: string;
  braintreeId: string;
  cardExpiry: string;
  cardNumber: string;
  insuredPartyId: string;
  phoneNumber: string;
  printDocuments: boolean;
  sortCode: string;
  documents: PolicyDocument[];
  coverType?: CoverType;
  correspondenceAddress: string;
}

export interface Policy {
  atlasLifecycleStatus: PolicyLifecycleStatus;
  correspondencePostcode: string;
  dob: string;
  email: string;
  historyId: number;
  id: number;
  policyDetailsId: string;
  policyEndDate: string;
  policyInceptionDate: string;
  policyInstances: PolicyInstance[];
  policyNumber: string;
  customerRenewalOptOut: boolean;
  policyDaysUntilRenewal: number;
  riskAddress: string;
  riskPostcode: string;
  surname: string;
  title: string;
  forename: string;
  phoneNumber: string;
  allowCustomerPreferenceAmendment: boolean;
  customerSince?: string;
}

export interface PolicyInstance {
  historyId: number;
  id: number;
  isAgentRenConversionAvailable: boolean;
  isCustomerRenConversionAvailable: boolean;
  isEditableRenAgentConversionAvailable: boolean;
  isEditableRenCustomerConversionAvailable: boolean;
  parentHistoryId: number;
  parentPolicyDetailsId: string;
  policyDetailsId: string;
  sortOrder: number;
  suppressInstanceFromPortal: boolean;
  suppressionDescription: string;
  renewalDaysUntilRenewal: number;
}

export interface Quote {
  agreementId: string;
  coverType: CoverType;
  dateSaved: string;
  quoteLink: string;
  quotePrice: number;
  quoteRef: string;
  quoteStatus: QuoteStatus;
  riskPostcode: string;
  startDate: string;
  policyDetailsId: string;
  historyId: number;
}

export const QUOTE_STATUS = {
  AVAILABLE: 'Quote',
  PURCHASED: 'Unavailable (Purchased)',
  REFERRED: 'Unavailable (Referred)',
  UNAVAILABLE: 'Unavailable'
} as const;

export type QuoteStatus = (typeof QUOTE_STATUS)[keyof typeof QUOTE_STATUS];

export const COVER_TYPE = {
  BUILDINGS_AND_CONTENTS: 'Buildings and Contents',
  CONTENTS: 'Contents',
  BUILDINGS: 'Buildings'
} as const;

export type CoverType = (typeof COVER_TYPE)[keyof typeof COVER_TYPE];

export const POLICY_LIFECYCLE_STATUS = {
  NOPOLICY: 'NOPOLICY',
  PROSPECT: 'PROSPECT',
  POLICY: 'POLICY',
  POLICY_NB_SCHEDULED: 'POLICY-NB-SCHEDULED',
  POLICY_NB_COOLING: 'POLICY-NB-COOLING',
  POLICY_RENEWAL_WINDOW: 'POLICY-RENEWAL-WINDOW',
  POLICY_RENEWAL_INVITEDNODOCUMENTS: 'POLICY-RENEWAL-INVITEDNODOCUMENTS',
  POLICY_RENEWAL_QUOTE: 'POLICY-RENEWAL-QUOTE',
  POLICY_RENEWAL_INVITE: 'POLICY-RENEWAL-INVITE',
  POLICY_RENEWAL_NOCONVERSION: 'POLICY-RENEWAL-NOCONVERSION',
  POLICY_RENINVITE_WITHDRAWN: 'POLICY-RENINVITE-WITHDRAWN',
  POLICY_RENEWAL_SCHEDULED: 'POLICY-RENEWAL-SCHEDULED',
  POLICY_RENEWAL_MTA: 'POLICY-RENEWAL-MTA',
  POLICY_RENEWAL_COOLING: 'POLICY-RENEWAL-COOLING',
  POLICY_MTAQUOTE: 'POLICY-MTAQUOTE',
  POLICY_MTAQUOTEINVITED: 'POLICY-MTAQUOTEINVITED',
  POLICY_MTA_NOCONVERSION: 'POLICY-MTA-NOCONVERSION',
  POLICY_MTA_SCHEDULED: 'POLICY-MTA-SCHEDULED',
  POLICY_MTA_COOLING: 'POLICY-MTA-COOLING',
  POLICY_REINSTATED_SCHEDULED: 'POLICY-REINSTATED-SCHEDULED',
  CANCEL: 'CANCEL',
  CANCEL_SCHEDULED: 'CANCEL-SCHEDULED',
  LAPSE: 'LAPSE',
  LAPSE_COOLING: 'LAPSE-COOLING',
  LAPSE_SCHEDULED: 'LAPSE-SCHEDULED',
  HISTORY_EXPIRED_QUOTEONLY: 'HISTORY-EXPIRED-QUOTEONLY',
  HISTORY_EXPIRED_POLICYINSTANCE: 'HISTORY-EXPIRED-POLICYINSTANCE',
  HISTORY_EXPIRED_PREVPOLICYINSTANCE: 'HISTORY-EXPIRED-PREVPOLICYINSTANCE',
  HISTORY_EXPIREDQUOTEONLY_PREVPOLICYINSTANCE: 'HISTORY-EXPIREDQUOTEONLY-PREVPOLICYINSTANCE',
  POLICY_LIVE_EXPIRING: 'POLICY-LIVE-EXPIRING'
} as const;

export type PolicyLifecycleStatus = (typeof POLICY_LIFECYCLE_STATUS)[keyof typeof POLICY_LIFECYCLE_STATUS];

export interface PolicyDocument {
  documentArchiveId: number;
  documentType: DocumentTypeCode;
  effectiveDate: string;
  endDate: string;
  policyBookletUrl: string;
  documentTemplateId: number;
  historyId: number;
  policyDetailsId: string;
}

export const DOCUMENT_TYPE_CODE = {
  PURCHASED_DOC: 'PURCHASED',
  ADJUSTED_DOC: 'ADJUSTED',
  RENEWED_DOC: 'RENEWED',
  RENEWAL_INVITE_DOC: 'RENEWAL_INVITE',
  BOOKLET: 'BOOKLET'
};

export type DocumentTypeCode = (typeof DOCUMENT_TYPE_CODE)[keyof typeof DOCUMENT_TYPE_CODE];
